import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import type { FormSnapshot } from '#services/api/insights.ts';
import type { loader } from './FormsDrilldown.data.ts';

import * as React from 'react';
import { Dialog } from '@mend/mui';
import { Tooltip } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { endOfDay, format, startOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useLoaderData, useSearchParams } from 'react-router';

import { FiltersDisclaimerDialog } from '#components/Drilldown/FiltersDisclaimerDialog.tsx';
import { Toolbar } from '#components/Drilldown/Toolbar.tsx';
import { SEARCH_MIN_CHARS } from '#components/Drilldown/Toolbar.utils.ts';
import { useClose } from '#components/Drilldown/use-close.ts';
import { useColumnVisibility } from '#components/Drilldown/use-column-visibility.ts';
import { formSnapshot } from '#lib/react-query/queries.ts';
import { convertUtcStringToLocalDate, FORMATS } from '#utils/dates.ts';
import { getMappedDateParams } from './FormsDrilldown.utils.ts';
import { FormsFiltersPanel } from './FormsFiltersPanel.tsx';

const statusMap = {
  assigned: {
    label: 'Assigned',
    description: "This form is assigned, but the patient hasn't opened it.",
  },
  opened: {
    label: 'Opened',
    description:
      'This form has been opened by the patient, but they have not completed any questions.',
  },
  started: {
    label: 'Started',
    description:
      'The patient has started to complete questions on this form, but it is not yet complete.',
  },
  completed: {
    label: 'Completed',
    description:
      'The patient completed this form. Staff has not yet reviewed it.',
  },
  reviewed: {
    label: 'Reviewed',
    description: 'This form has been completed and reviewed by staff.',
  },
} as const;

const columns: GridColDef<FormSnapshot>[] = [
  // Form Session Info
  {
    field: 'id',
    headerName: 'Form Session ID',
    description: 'Unique Mend identifier for the form session',
    width: 140,
  },
  // Organization Info
  {
    field: 'masterOrgId',
    headerName: 'Master Org ID',
    description: 'ID of the top-level organization in the org hierarchy',
    valueFormatter: (value: FormSnapshot['masterOrgId']) => {
      return value ? String(value) : '--';
    },
    width: 120,
  },
  {
    field: 'masterOrgName',
    headerName: 'Master Org Name',
    description: 'Name of the top-level organization',
    valueFormatter: (value: FormSnapshot['masterOrgName']) => {
      return value ? String(value) : '--';
    },
    width: 150,
  },
  {
    field: 'orgId',
    headerName: 'Org ID',
    description: 'ID of the sub-organization where the form occurred',
    valueFormatter: (value: FormSnapshot['orgId']) => {
      return value ? String(value) : '--';
    },
    width: 70,
  },
  {
    field: 'orgName',
    headerName: 'Organization',
    description: 'Name of the sub-organization',
    valueFormatter: (value: FormSnapshot['orgName']) => {
      return value ? String(value) : '--';
    },
    width: 110,
  },
  // Provider Info
  {
    field: 'providerId',
    headerName: 'Provider ID',
    description:
      'Unique Mend identifier for the provider associated with the appointment',
    valueFormatter: (value: FormSnapshot['providerId']) => {
      return value ? String(value) : '--';
    },
    width: 100,
  },
  {
    field: 'providerFirstName',
    headerName: 'Provider First Name',
    description: 'First name of the provider responsible for the form',
    valueFormatter: (value: FormSnapshot['providerFirstName']) => {
      if (!value) return '--';
      return String(value);
    },
    width: 160,
  },
  {
    field: 'providerLastName',
    headerName: 'Provider Last Name',
    description: 'Last name of the provider responsible for the form',
    valueFormatter: (value: FormSnapshot['providerLastName']) => {
      if (!value) return '--';
      return String(value);
    },
    width: 160,
  },
  {
    field: 'providerPreferredFirstName',
    headerName: 'Provider Preferred First Name',
    description:
      'Preferred first name of the provider responsible for the form',
    valueFormatter: (value: FormSnapshot['providerPreferredFirstName']) => {
      return value ? String(value) : '--';
    },
    width: 240,
  },
  {
    field: 'providerSpecialty',
    headerName: 'Provider Specialty',
    description: 'Medical specialty of the provider',
    valueFormatter: (value: FormSnapshot['providerSpecialty']) => {
      return value ? String(value) : '--';
    },
    width: 150,
  },
  {
    field: 'providerCredential',
    headerName: 'Provider Credential',
    description: "Provider's professional designation",
    valueFormatter: (value: FormSnapshot['providerCredential']) => {
      return value ? String(value) : '--';
    },
    width: 160,
  },
  // Assignee Info
  {
    field: 'assigneeId',
    headerName: 'Assignee ID',
    description: 'Unique Mend ID of the person assigned to complete the form',
    valueFormatter: (value: FormSnapshot['assigneeId']) => {
      return value ? String(value) : '--';
    },
    width: 110,
  },
  {
    field: 'assigneeChartId',
    headerName: 'Assignee Chart ID',
    description: 'Patient chart ID for the assignee',
    valueFormatter: (value: FormSnapshot['assigneeChartId']) => {
      return value ? String(value) : '--';
    },
    width: 150,
  },
  {
    field: 'assigneeFirstName',
    headerName: 'Assignee First Name',
    description: 'First name of the individual assigned to the form',
    valueFormatter: (value: FormSnapshot['assigneeFirstName']) => {
      if (!value) return '--';
      return String(value);
    },
    width: 170,
  },
  {
    field: 'assigneeLastName',
    headerName: 'Assignee Last Name',
    description: 'Last name of the individual assigned to the form',
    valueFormatter: (value: FormSnapshot['assigneeLastName']) => {
      if (!value) return '--';
      return String(value);
    },
    width: 170,
  },
  {
    field: 'assigneePreferredFirstName',
    headerName: 'Assignee Preferred First Name',
    description: 'Preferred first name of the individual assigned to the form',
    valueFormatter: (value: FormSnapshot['assigneePreferredFirstName']) => {
      return value ? String(value) : '--';
    },
    width: 240,
  },
  // Subject Info
  {
    field: 'subjectId',
    headerName: 'Subject ID',
    description: 'Unique Mend ID of the individual the form is about',
    valueFormatter: (value: FormSnapshot['subjectId']) => {
      return value ? String(value) : '--';
    },
    width: 100,
  },
  {
    field: 'subjectChartId',
    headerName: 'Subject Chart ID',
    description: 'Patient chart ID for the subject',
    valueFormatter: (value: FormSnapshot['subjectChartId']) => {
      return value ? String(value) : '--';
    },
    width: 140,
  },
  {
    field: 'subjectFirstName',
    headerName: 'Subject First Name',
    description:
      'First name of the person the form is about. This is typically the individual selected in the assignment modal.',
    valueFormatter: (value: FormSnapshot['subjectFirstName']) => {
      if (!value) return '--';
      return String(value);
    },
    width: 160,
  },
  {
    field: 'subjectLastName',
    headerName: 'Subject Last Name',
    description:
      'Last name of the person the form is about. This is typically the individual selected in the assignment modal.',
    valueFormatter: (value: FormSnapshot['subjectLastName']) => {
      if (!value) return '--';
      return String(value);
    },
    width: 160,
  },
  {
    field: 'subjectPreferredFirstName',
    headerName: 'Subject Preferred First Name',
    description:
      'Preferred first name of the person the form is about. This is typically the individual selected in the assignment modal.',
    valueFormatter: (value: FormSnapshot['subjectPreferredFirstName']) => {
      return value ? String(value) : '--';
    },
    width: 230,
  },
  // Reviewer Info
  {
    field: 'reviewerId',
    headerName: 'Reviewer ID',
    description: 'Staff Mend ID of the person who reviewed the form',
    valueFormatter: (value: FormSnapshot['reviewerId']) => {
      return value ? String(value) : '--';
    },
    width: 110,
  },
  {
    field: 'reviewerFirstName',
    headerName: 'Reviewer First Name',
    description: 'First name of the reviewer',
    valueFormatter: (value: FormSnapshot['reviewerFirstName']) => {
      return value ? String(value) : '--';
    },
    width: 170,
  },
  {
    field: 'reviewerLastName',
    headerName: 'Reviewer Last Name',
    description: 'Last name of the reviewer',
    valueFormatter: (value: FormSnapshot['reviewerLastName']) => {
      return value ? String(value) : '--';
    },
    width: 170,
  },
  {
    field: 'reviewerPreferredFirstName',
    headerName: 'Reviewer Preferred First Name',
    description: 'Preferred first name of the reviewer',
    valueFormatter: (value: FormSnapshot['reviewerPreferredFirstName']) => {
      return value ? String(value) : '--';
    },
    width: 240,
  },
  // Assessment Type
  {
    field: 'assessmentTypeId',
    headerName: 'Form Type ID',
    description: 'Unique Mend ID for the type of form',
    valueFormatter: (value: FormSnapshot['assessmentTypeId']) => {
      return value ? String(value) : '--';
    },
    width: 120,
  },
  {
    field: 'assessmentTypeName',
    headerName: 'Form Type Name',
    description: 'Descriptive name of the form',
    valueFormatter: (value: FormSnapshot['assessmentTypeName']) => {
      return value ? String(value) : '--';
    },
    width: 140,
  },
  {
    field: 'assessmentTypeTitle',
    headerName: 'Form Type Title',
    description: 'Descriptive title of the form',
    valueFormatter: (value: FormSnapshot['assessmentTypeTitle']) => {
      return value ? String(value) : '--';
    },
    width: 130,
  },
  // Assignment Info
  {
    field: 'assignmentSource',
    headerName: 'Assignment Source',
    description:
      'Source that triggered the assignment (e.g., via appointment type, booking, or manually/adhoc)',
    valueFormatter: (value: FormSnapshot['assignmentSource']) => {
      return value === 'adhoc' ? 'Ad Hoc' : 'Appointment';
    },
    width: 160,
  },
  {
    field: 'created',
    headerName: 'Assignment Date (Created)',
    description: 'Date and time when the form was assigned',
    valueGetter: (value: FormSnapshot['created']) => {
      return convertUtcStringToLocalDate(value);
    },
    valueFormatter: (value: Date) => {
      return format(value, 'MM/dd/yyyy h:mmaaa');
    },
    width: 220,
  },
  // Engagement Timeline
  // TODO: As of TS-15223, it's not yet returned from the API
  // {
  //   field: 'startedDate',
  //   renderHeader: () => (
  //     <Tooltip title="Timestamp when the form was first opened by the patient">
  //       <span>Started Date</span>
  //     </Tooltip>
  //   ),
  //   valueGetter: (value: string | null | undefined) => {
  //     return value ? convertUtcStringToLocalDate(value) : null;
  //   },
  //   valueFormatter: (value: Date | null) => {
  //     return value ? format(value, 'MM/dd/yyyy h:mmaaa') : '--';
  //   },
  // },
  {
    field: 'status',
    headerName: 'Status',
    description:
      'Current stage of the form (e.g., Pending, Assigned, Started, Awaiting Review, Completed Review)',
    valueGetter: (value: FormSnapshot['status']) => statusMap[value].label,
    renderCell: (
      params: GridRenderCellParams<
        FormSnapshot,
        (typeof statusMap)[keyof typeof statusMap]['label']
      >
    ) => (
      <Tooltip title={statusMap[params.row.status].description}>
        <span>{params.value}</span>
      </Tooltip>
    ),
    width: 70,
  },
  // Severity and Scoring
  {
    field: 'severity',
    headerName: 'Severity',
    description: 'Severity level based on scoring logic',
    valueFormatter: (value: FormSnapshot['severity']) => {
      return value !== null ? String(value) : '--';
    },
    width: 80,
  },
  {
    field: 'scoring',
    headerName: 'Scoring Info',
    description: 'Structured scoring data from the form (in JSON format)',
    valueFormatter: (value: FormSnapshot['scoring']) => (value ? value : '--'),
    width: 110,
  },
  // Address Info
  {
    field: 'addressId',
    headerName: 'Address ID',
    description:
      'Mend ID of the address associated with the assigning appointment',
    valueFormatter: (value: FormSnapshot['addressId']) => {
      return value ? String(value) : '--';
    },
    width: 100,
  },
  {
    field: 'addressType',
    headerName: 'Address Type',
    description: 'Type of address (e.g., facility address, provider on-site)',
    valueFormatter: (value: FormSnapshot['addressType']) => {
      return value ? String(value) : '--';
    },
    width: 120,
  },
  {
    field: 'addressLocationName',
    headerName: 'Location Name',
    description: 'Name of the location where the appointment took place',
    valueFormatter: (value: FormSnapshot['addressLocationName']) => {
      return value ? String(value) : '--';
    },
    width: 130,
  },
  {
    field: 'addressStreet',
    headerName: 'Location Street',
    description: 'Street address where the appointment took place',
    valueFormatter: (value: FormSnapshot['addressStreet']) => {
      return value ? String(value) : '--';
    },
    width: 130,
  },
  {
    field: 'addressCity',
    headerName: 'Location City',
    description: 'City where the appointment took place',
    valueFormatter: (value: FormSnapshot['addressCity']) => {
      return value ? String(value) : '--';
    },
    width: 120,
  },
  {
    field: 'addressState',
    headerName: 'Location State',
    description: 'State where the appointment took place',
    valueFormatter: (value: FormSnapshot['addressState']) => {
      return value ? String(value) : '--';
    },
    width: 120,
  },
  {
    field: 'addressPostal',
    headerName: 'Location Postal',
    description: 'Postal code where the appointment took place',
    valueFormatter: (value: FormSnapshot['addressPostal']) => {
      return value ? String(value) : '--';
    },
    width: 130,
  },
  // Cancellation Info
  {
    field: 'canceled',
    headerName: 'Canceled',
    description: 'Indicates if the assignment was canceled',
    valueFormatter: (value: FormSnapshot['canceled']) => {
      return value ? 'Yes' : 'No';
    },
    width: 90,
  },
  {
    field: 'canceledDate',
    headerName: 'Canceled Date',
    description: 'Timestamp when the assignment was canceled',
    valueGetter: (value: FormSnapshot['canceledDate']) => {
      return value ? convertUtcStringToLocalDate(value) : null;
    },
    valueFormatter: (value: Date | null) => {
      return value ? format(value, 'MM/dd/yyyy h:mmaaa') : '--';
    },
    width: 130,
  },
  // Completion Info
  {
    field: 'completed',
    headerName: 'Completed',
    description: 'Indicates if the form was completed',
    valueFormatter: (value: FormSnapshot['completed']) => {
      return value ? 'Yes' : 'No';
    },
    width: 100,
  },
  {
    field: 'completedDate',
    headerName: 'Completed Date',
    description: 'Timestamp of form completion',
    valueGetter: (value: FormSnapshot['completedDate']) => {
      return value ? convertUtcStringToLocalDate(value) : null;
    },
    valueFormatter: (value: Date | null) => {
      return value ? format(value, 'MM/dd/yyyy h:mmaaa') : '--';
    },
    width: 140,
  },
  // Deleted Status
  {
    field: 'deleted',
    headerName: 'Deleted',
    description: 'Indicates if the assignment has been deleted',
    valueFormatter: (value: FormSnapshot['deleted']) => {
      return value ? 'Yes' : 'No';
    },
    width: 80,
  },
  // Reviewed Info
  {
    field: 'reviewed',
    headerName: 'Reviewed',
    description: 'Indicates if the form has been reviewed by staff',
    valueFormatter: (value: FormSnapshot['reviewed']) => {
      return value ? 'Yes' : 'No';
    },
    width: 90,
  },
  {
    field: 'reviewedDate',
    headerName: 'Reviewed Date',
    description: 'Timestamp when the form was reviewed',
    valueGetter: (value: FormSnapshot['reviewedDate']) => {
      return value ? convertUtcStringToLocalDate(value) : null;
    },
    valueFormatter: (value: Date | null) => {
      return value ? format(value, 'MM/dd/yyyy h:mmaaa') : '--';
    },
    width: 130,
  },
  // Maintenance and Versioning
  {
    field: 'updated',
    headerName: 'Last Updated Date',
    description: 'Most recent timestamp of any update to the form session',
    valueGetter: (value: FormSnapshot['updated']) => {
      return value ? convertUtcStringToLocalDate(value) : null;
    },
    valueFormatter: (value: Date | null) => {
      return value ? format(value, 'MM/dd/yyyy h:mmaaa') : '--';
    },
    width: 150,
  },
  {
    field: 'snapshotDate',
    headerName: 'Snapshot Date',
    description:
      'Date when the snapshot of the data was taken (for use in reporting/analytics)',
    valueGetter: (value: FormSnapshot['snapshotDate']) => {
      return convertUtcStringToLocalDate(value);
    },
    valueFormatter: (value: Date) => {
      return format(value, 'MM/dd/yyyy h:mmaaa');
    },
    width: 130,
  },
];

const defaultData: FormSnapshot[] = [];
const pageSizeOptions = [10, 25, 50, 100, 250, 500, 1_000, 5_000, 10_000];

export function FormsDrilldown() {
  const data = useLoaderData<Awaited<ReturnType<typeof loader>>>();

  const [, setSearchParams] = useSearchParams();

  const columnVisibility = useColumnVisibility({
    storageKey: 'forms-drilldown-column-visibility',
    defaultVisibility: {
      masterOrgId: false,
      masterOrgName: false,
      providerPreferredFirstName: false,
      providerSpecialty: false,
      providerCredential: false,
      assigneePreferredFirstName: false,
      subjectPreferredFirstName: false,
      reviewerPreferredFirstName: false,
      assessmentTypeId: false,
      assessmentTypeName: false,
      assignmentSource: false,
      scoring: false,
      addressId: false,
      addressType: false,
      addressLocationName: false,
      addressStreet: false,
      addressCity: false,
      addressState: false,
      addressPostal: false,
      canceled: false,
      completed: false,
      reviewed: false,
    },
  });

  const mappedDateParams = getMappedDateParams(data.source);

  const query = useQuery(
    formSnapshot({
      page: data.page,
      pageSize: data.pageSize,
      sortBy: data.sortBy,
      // Add an asterisk if needed to trigger wildcard search in the API
      searchText: data.searchText ? `${data.searchText}*` : undefined,
      [mappedDateParams.from]: formatInTimeZone(
        startOfDay(data.from),
        'UTC',
        FORMATS.MYSQL_DATETIME
      ),
      [mappedDateParams.to]: formatInTimeZone(
        endOfDay(data.to),
        'UTC',
        FORMATS.MYSQL_DATETIME
      ),
      providerId: data.providerIds,
      assessmentTypeId: data.assessmentTypeIds,
      assessmentTypeRequired:
        data.formsPriority === 'all'
          ? undefined
          : data.formsPriority === 'required'
            ? 1
            : 0,
    })
  );

  const [rowCount, setRowCount] = React.useState(0);

  if (query.data !== undefined && query.data.totalItems !== rowCount) {
    setRowCount(query.data.totalItems);
  }

  const titleId = React.useId();

  const close = useClose({ drilldownType: 'forms' });

  return (
    <>
      <Dialog.Root
        open
        onClose={(reason) =>
          close.handler({
            force: reason === 'error',
            doNotShowFiltersDisclaimerAgain: false,
          })
        }
        fullWidth
        fullHeight
        maxWidth="xl"
        aria-labelledby={titleId}
      >
        <Dialog.Header>
          <Dialog.Title id={titleId}>Forms Drilldown</Dialog.Title>
        </Dialog.Header>
        <Dialog.Content>
          <DataGridPro
            rows={query.data?.data ?? defaultData}
            rowCount={rowCount}
            columns={columns}
            columnVisibilityModel={columnVisibility.model}
            onColumnVisibilityModelChange={columnVisibility.onChange}
            initialState={data.dataGridInitialState}
            loading={query.isLoading}
            showToolbar
            checkboxSelection
            disableRowSelectionOnClick
            pagination
            paginationMode="server"
            onPaginationModelChange={(model) => {
              setSearchParams((searchParams) => {
                searchParams.set('page', String(model.page + 1));
                searchParams.set('pageSize', String(model.pageSize));
                return searchParams;
              });
            }}
            pageSizeOptions={pageSizeOptions}
            sortingMode="server"
            onSortModelChange={(model) => {
              const filteredItems = model.filter(
                (model) => typeof model.sort === 'string'
              );

              if (filteredItems.length === 0) {
                setSearchParams((searchParams) => {
                  searchParams.delete('sortBy');
                  return searchParams;
                });
                return;
              }

              setSearchParams((searchParams) => {
                searchParams.set(
                  'sortBy',
                  filteredItems
                    // Safe to use `!` as long as the filter criteria matches
                    .map((item) => `${item.field} ${item.sort!.toUpperCase()}`)
                    .join(',')
                );
                return searchParams;
              });
            }}
            filterMode="server"
            onFilterModelChange={(model) => {
              // The values come trimmed
              const searchText = model.quickFilterValues?.join(' ') ?? '';

              setSearchParams((searchParams) => {
                if (searchText.length < SEARCH_MIN_CHARS) {
                  searchParams.delete('searchText');
                } else {
                  searchParams.set('searchText', searchText);
                }
                return searchParams;
              });
            }}
            slots={{
              toolbar: Toolbar,
              filterPanel: FormsFiltersPanel,
            }}
          />
        </Dialog.Content>
      </Dialog.Root>

      {close.showFiltersDisclaimer && (
        <FiltersDisclaimerDialog
          onClose={close.dismissFiltersDisclaimer}
          onOk={(doNotShowAgain) =>
            close.handler({
              force: true,
              doNotShowFiltersDisclaimerAgain: doNotShowAgain,
            })
          }
        />
      )}
    </>
  );
}
